import React, { useState } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";

// GAIB Header Component
const GAIBHeader = () => (
  <div className="flex items-center justify-center mb-8">
    <img
      src="/lovable-uploads/61a722fd-1842-4dd1-9fe5-80ed4f5ce6e7.png"
      alt="GAIB Logo"
      className="h-8 md:h-10 w-auto"
    />
  </div>
);

const LoginForm = ({ onLogin, loading }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    setError("");

    if (!email || !password) {
      setError("Please fill in all fields");
      return;
    }

    try {
      await onLogin(email, password);
    } catch (err) {
      setError(err.message || "Failed to login");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-200 via-orange-300 to-orange-400 relative overflow-hidden">
      {/* Layout Background Elements */}
      <img
        src="/lovable-uploads/1a8cc40d-5db4-4af8-892a-0d9d139ffb8c.png"
        alt=""
        className="clouds-top-right"
        aria-hidden="true"
      />
      <img
        src="/lovable-uploads/96315555-c76f-42b9-a5f7-403e312f654e.png"
        alt=""
        className="circles-top-left"
        aria-hidden="true"
      />
      <img
        src="/lovable-uploads/cce19565-8e57-4e6e-87b3-8a5ba9ee91c9.png"
        alt=""
        className="noise-overlay"
        aria-hidden="true"
      />
      <img
        src="/lovable-uploads/8a797feb-705c-4580-a886-fff6a268cf33.png"
        alt=""
        className="desert-bottom"
        aria-hidden="true"
      />

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <GAIBHeader />

          {/* Main Glassmorphism Card */}
          <div className="bg-white/10 backdrop-blur-xl p-8 rounded-3xl border border-white/30 shadow-2xl max-w-sm mx-auto relative overflow-hidden">
            {/* Inner glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-3xl"></div>

            {/* Content */}
            <div className="relative z-10">
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-black mb-3 drop-shadow-sm">
                  Admin Login
                </h1>
                <p className="text-black/70 text-sm">
                  Sign in to access the admin dashboard
                </p>
              </div>

              <div className="space-y-6">
                <div className="space-y-3">
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold text-black"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="w-full px-4 py-3 border border-white/30 rounded-xl bg-white/20 backdrop-blur-sm text-black placeholder-black/50 focus:outline-none focus:ring-2 focus:ring-[#19E3E3]/50 focus:border-[#19E3E3]/50 transition-all duration-300 hover:bg-white/30"
                    placeholder="admin@gaib.ai"
                    disabled={loading}
                  />
                </div>

                <div className="space-y-3">
                  <label
                    htmlFor="password"
                    className="block text-sm font-semibold text-black"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="w-full px-4 py-3 pr-12 border border-white/30 rounded-xl bg-white/20 backdrop-blur-sm text-black placeholder-black/50 focus:outline-none focus:ring-2 focus:ring-[#19E3E3]/50 focus:border-[#19E3E3]/50 transition-all duration-300 hover:bg-white/30"
                      placeholder="Enter your password"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-black/50 hover:text-black/80 transition-colors duration-200"
                      disabled={loading}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="text-red-700 text-sm bg-red-50/80 backdrop-blur-sm border border-red-200/50 rounded-xl p-3">
                    {error}
                  </div>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full py-3 px-4 bg-[#19E3E3]/80 hover:bg-[#19E3E3] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg backdrop-blur-sm border border-white/20"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Signing in...
                    </div>
                  ) : (
                    "Sign In"
                  )}
                </button>

                {/* Additional glassmorphism elements */}
                <div className="flex justify-center space-x-4 pt-4">
                  <div className="w-2 h-2 bg-white/30 rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-white/30 rounded-full animate-pulse delay-200"></div>
                  <div className="w-2 h-2 bg-white/30 rounded-full animate-pulse delay-400"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
